import { CANVAS_INSPECT_BLOCK_LIMIT_MAX } from '@hector/shared-types';
import { blocsVersMarkdown, type BlocSerialisable } from '../editor/blocsVersMarkdown';
import { readCanvasSnapshot } from './canvas-agent/canvasSnapshot';
import type { SuperDocDocumentApi } from './canvas-agent/superDocDocumentApi';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { useCanvasEditorStore } from './canvasEditorStore';

/**
 * capturePlanDuCanvas — le plan TEL QU'IL EST À L'ÉCRAN, au clic « Lancer ».
 *
 * ## Pourquoi ce module existe
 *
 * `generation-only` repartait du markdown FIGÉ produit par le job `research`
 * (`StartGenerationOnlyJob` relit `research:{jobId}` en Redis, une clé qu'aucun
 * chemin ne réécrit). Toutes les corrections que l'avocat apporte au plan dans
 * le canvas étaient donc perdues — et l'acte généré écrasait ensuite le
 * brouillon corrigé, effaçant jusqu'à la trace du travail. Le champ
 * `redaction_plan_response.plan_markdown` existe pour ça
 * (`packages/shared-types/src/casediscovery/redactionFrames.ts`) ; ce module le
 * remplit.
 *
 * ## Pourquoi la lecture vient du NAVIGATEUR
 *
 * C'est le client qui tient l'éditeur, et le clic se produit chez lui. Même
 * chemin que l'add-in Word, qui POSTe depuis toujours son document relu. Une
 * lecture tirée depuis le backend coûterait un aller-retour et échouerait
 * précisément quand le canvas est fermé.
 *
 * ## Pourquoi `readCanvasSnapshot` plutôt qu'un `doc.blocks.list` écrit ici
 *
 * Lire les blocs « à la main » paraît deux lignes moins cher, et c'est le piège
 * exact de #1008 : la bonne source du texte est `text`, pas `textPreview` que
 * SuperDoc coupe à ~78 caractères — un balayage écrit à côté de l'existant
 * aurait re-commis la même faute et produit un plan en ellipses. La
 * normalisation des blocs a UN seul propriétaire
 * (`canvas-agent/canvasSnapshot.ts`) ; elle en garde un. Le coût assumé est
 * celui des lectures annexes du snapshot (corps SDM, commentaires, notes,
 * révisions) : il est payé UNE fois par lancement, jamais dans une boucle.
 */

/**
 * Fenêtre de lecture, au plafond du contrat partagé (250).
 *
 * ⚠️ Un plan détaillé fait ~29 000 caractères ≈ 340 blocs (86 caractères par
 * bloc, relevé cité par `canvas-agent/canvasSnapshot.ts`) : une seule page NE
 * SUFFIT PAS, et s'arrêter au premier appel tronquerait le plan en silence.
 * D'où la boucle ci-dessous, calquée sur `canvas-agent/selectorResolver.ts`
 * (`listBlocks`), qui balaie déjà le document entier de la même façon.
 */
const TAILLE_DE_PAGE = CANVAS_INSPECT_BLOCK_LIMIT_MAX;

/**
 * Garde anti-boucle-infinie : 40 pages = 10 000 blocs ≈ 330 pages de document,
 * très au-delà du plus long acte observé (28 pages / 852 blocs).
 *
 * WHY une borne du tout : la condition d'arrêt dépend du fournisseur (« cette
 * page est moins pleine que demandée »). Un fournisseur qui resservirait
 * toujours une page pleine — offset ignoré, page en cache — ferait tourner la
 * boucle sans fin au moment du clic, l'onglet figé, sans erreur.
 */
const PAGES_MAX = 40;

/** Journalise le repli — un repli MUET est exactement le défaut qu'on corrige. */
function journaliserLeRepli(raison: string, detail?: unknown): undefined {
  // console.warn et pas .error : la génération continue, elle repart du plan du
  // job parent. C'est une dégradation nommée, pas une panne.
  console.warn(
    `[plan_markdown] Plan du canvas NON capturé (${raison}). ` +
    'Le backend repartira du plan figé du job de recherche : les corrections faites ' +
    'dans le canvas ne seront pas reprises par la génération.',
    ...(detail === undefined ? [] : [detail]),
  );
  return undefined;
}

/**
 * Sérialise en markdown TOUS les blocs du document fourni.
 *
 * Rend `undefined` — jamais une exception, jamais une chaîne partielle — dès
 * que la capture n'est pas intégralement digne de confiance : le contrat avec
 * l'appelant est « le champ part complet, ou il ne part pas ». `undefined`
 * laisse le backend sur le plan du job parent, c'est-à-dire sur le comportement
 * d'avant ce module.
 */
export async function capturerLePlanDepuisLeDocument(
  doc: SuperDocDocumentApi | null,
): Promise<string | undefined> {
  // `blocks.list` absent = runtime sans Document API (coque v1, éditeur en
  // cours de montage) : la même porte que celle de `canvas_inspect`.
  if (!doc || typeof doc.blocks?.list !== 'function') {
    return journaliserLeRepli('aucun document SuperDoc monté, ou Document API sans blocks.list');
  }

  const blocs: BlocSerialisable[] = [];
  try {
    for (let page = 0; page < PAGES_MAX; page += 1) {
      const snapshot = await readCanvasSnapshot(doc, { offset: page * TAILLE_DE_PAGE, limit: TAILLE_DE_PAGE });
      for (const bloc of snapshot.blocks) {
        blocs.push({
          // `fullText` et NON `text` : ce dernier est borné à 2 000 caractères
          // pour le contexte du modèle (#1008). Ici il n'y a pas de modèle à
          // ménager — c'est le plan de l'avocat qui doit partir entier.
          text: bloc.fullText,
          nodeType: bloc.nodeType,
          headingLevel: bloc.headingLevel,
          styleId: bloc.effectiveStyle?.styleId,
        });
      }
      // Arrêt sur le compte BRUT, comme `selectorResolver.listBlocks` : un bloc
      // malformé est écarté de `snapshot.blocks` mais occupe bien une place de
      // page chez le fournisseur. S'arrêter sur la longueur normalisée ferait
      // conclure « fin du document » au premier bloc rejeté.
      if (snapshot.rawBlocksCount < TAILLE_DE_PAGE) {
        const markdown = blocsVersMarkdown(blocs);
        // Canvas monté mais vide (ou n'ayant que des blocs vides) : envoyer une
        // chaîne vide ferait taire le plan du job parent sans rien mettre à la
        // place. Le backend ignore déjà le vide ; on ne l'envoie pas du tout.
        return markdown.trim().length > 0
          ? markdown
          : journaliserLeRepli(`document monté mais sans texte (${blocs.length} bloc(s) lu(s), tous vides)`);
      }
    }
    // Borne atteinte : on NE PEUT PAS savoir si le document est réellement
    // gigantesque ou si le fournisseur ignore l'offset. Envoyer les 10 000
    // premiers blocs, ce serait tronquer en silence — précisément ce que la
    // pagination est là pour empêcher. On préfère l'ancien comportement.
    return journaliserLeRepli(
      `garde-fou de pagination atteint (${PAGES_MAX} pages de ${TAILLE_DE_PAGE} blocs) sans fin de document`,
    );
  } catch (error) {
    // Une lecture qui rejette ne doit JAMAIS faire échouer le lancement : le
    // clic « Lancer » de l'avocat vaut plus que la capture qui l'accompagne.
    return journaliserLeRepli('la lecture des blocs a échoué', error);
  }
}

/**
 * Le Document API du canvas actuellement monté, ou `null`.
 *
 * Même expression que les autres lecteurs du dépôt (`WebSocketPool`
 * `canvas_state_request`, `trackChangesReview.ts`) : le `.d.ts` de SuperDoc
 * type `doc` avec des entrées strictes par opération, là où la surface de
 * lecture n'en consomme qu'un sous-ensemble volontairement lâche.
 */
export function lireLeDocumentDuCanvasMonte(): SuperDocDocumentApi | null {
  return (
    (useCanvasEditorStore.getState().instance?.activeEditor?.doc as unknown as SuperDocDocumentApi | undefined) ?? null
  );
}

/** Capture le plan du canvas monté — point d'entrée du clic « Lancer ». */
export function capturerLePlanDuCanvasMonte(): Promise<string | undefined> {
  return capturerLePlanDepuisLeDocument(lireLeDocumentDuCanvasMonte());
}

/**
 * Capture the plan owned by one logical document session.
 *
 * A mounted/visible editor is only a projection in the multi-session runtime:
 * the scope must remain the authority both before and after the asynchronous
 * pagination of the document.
 */
export async function capturerLePlanDuScope(conversationId: string): Promise<string | undefined> {
  const scope = canvasDocumentRuntime.getScope(conversationId);
  const instance = scope?.getPhysicalResources().instance ?? null;
  const document = (instance?.activeEditor?.doc as unknown as SuperDocDocumentApi | undefined) ?? null;
  const markdown = await capturerLePlanDepuisLeDocument(document);
  return canvasDocumentRuntime.getScope(conversationId) === scope
    && scope?.getPhysicalResources().instance === instance
    ? markdown
    : undefined;
}
